import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CartContext, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Truck, CreditCard, Lock } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];
const NZ_REGIONS = ['AUK', 'WGN', 'CAN', 'WKO', 'BOP', 'OTA', 'MWT', 'NTL', 'HKB', 'TKI', 'GIS', 'STL', 'MBH', 'NSN', 'TAS', 'WTC'];

export default function CheckoutPage() {
  const { cart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [tdBookingId, setTdBookingId] = useState(null);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedSavedAddress, setSelectedSavedAddress] = useState('');
  const [creditAccount, setCreditAccount] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
  });
  
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
  });

  // Load user's saved addresses and credit account on mount
  useEffect(() => {
    if (user) {
      loadSavedAddresses();
      loadCreditAccount();
    }
  }, [user]);

  const loadSavedAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = res.data;
      let addresses = [];
      
      // Check for addresses array (newer format)
      if (userData.addresses && userData.addresses.length > 0) {
        addresses = userData.addresses;
      }
      // Check for shipping_address string (older format)
      else if (userData.shipping_address && typeof userData.shipping_address === 'string') {
        const parts = userData.shipping_address.split(', ');
        if (parts.length >= 4) {
          addresses.push({
            id: 'legacy',
            type: 'shipping',
            label: 'Saved Address',
            street: parts[0],
            city: parts[1],
            state: parts[2],
            postcode: parts[3],
            country: 'Australia',
            is_default: true
          });
        }
      }
      // Check for shipping_address object (another format)
      else if (userData.shipping_address && typeof userData.shipping_address === 'object') {
        addresses.push({
          id: 'legacy',
          type: 'shipping',
          label: 'Saved Address',
          ...userData.shipping_address,
          is_default: true
        });
      }
      
      setSavedAddresses(addresses);
      
      // Auto-fill with default address
      const defaultAddr = addresses.find(a => a.is_default) || addresses[0];
      if (defaultAddr) {
        setShippingAddress({
          street: defaultAddr.street || '',
          city: defaultAddr.city || '',
          state: defaultAddr.state || '',
          postcode: defaultAddr.postcode || '',
          country: defaultAddr.country || 'Australia',
        });
        setSelectedSavedAddress(defaultAddr.id);
      }
    } catch (err) {
      console.error('Error loading addresses:', err);
    }
  };

  const loadCreditAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/my-credit-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.has_credit_account) {
        setCreditAccount(res.data.account);
      }
    } catch (err) {
      console.error('Error loading credit account:', err);
    }
  };

  const handleSelectSavedAddress = (addressId) => {
    setSelectedSavedAddress(addressId);
    if (addressId === 'new') {
      setShippingAddress({
        street: '',
        city: '',
        state: '',
        postcode: '',
        country: 'Australia',
      });
      return;
    }
    const addr = savedAddresses.find(a => a.id === addressId);
    if (addr) {
      setShippingAddress({
        street: addr.street || '',
        city: addr.city || '',
        state: addr.state || '',
        postcode: addr.postcode || '',
        country: addr.country || 'Australia',
      });
    }
  };

  // Handle address autocomplete selection
  const handleShippingAddressSelect = (addressData) => {
    setShippingAddress({
      street: addressData.street || '',
      city: addressData.city || '',
      state: addressData.state || '',
      postcode: addressData.postcode || '',
      country: addressData.country || 'Australia',
    });
    setSelectedSavedAddress('new'); // Clear saved address selection
  };

  const handleBillingAddressSelect = (addressData) => {
    setBillingAddress({
      street: addressData.street || '',
      city: addressData.city || '',
      state: addressData.state || '',
      postcode: addressData.postcode || '',
      country: addressData.country || 'Australia',
    });
  };

  const isNZShipping = shippingAddress.country === 'New Zealand';
  const isNZBilling = billingAddress.country === 'New Zealand';
  const shippingStates = isNZShipping ? NZ_REGIONS : AU_STATES;
  const billingStates = isNZBilling ? NZ_REGIONS : AU_STATES;

  useEffect(() => {
    if (!cart.items?.length) {
      navigate('/cart');
    }
  }, [cart, navigate]);

  const calculateShipping = async () => {
    if (!shippingAddress.postcode || shippingAddress.postcode.length < 4) return;
    
    try {
      const res = await axios.post(`${API}/shipping/calculate`, {
        postcode: shippingAddress.postcode,
        suburb: shippingAddress.city || '',
        state: shippingAddress.state || '',
        items: cart.items.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
      });
      setShippingMethods(res.data.methods || []);
      setTdBookingId(res.data.booking_id || null);
      // Auto-select cheapest non-pickup option, or first
      const nonPickup = (res.data.methods || []).filter(m => m.service_code !== 'local_pickup');
      if (nonPickup.length > 0) {
        setSelectedShipping(nonPickup[0]);
      } else if (res.data.methods?.length > 0) {
        setSelectedShipping(res.data.methods[0]);
      }
    } catch (err) {
      console.error('Error calculating shipping:', err);
    }
  };

  useEffect(() => {
    if (shippingAddress.postcode?.length >= 4) {
      calculateShipping();
    }
  }, [shippingAddress.postcode, shippingAddress.city, shippingAddress.state, cart.items]);

  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'cash', or 'account'

  // Check if user can pay on account
  const canPayOnAccount = creditAccount && creditAccount.status === 'active' && creditAccount.available_credit >= (cart.subtotal + (selectedShipping?.cost || 0));
  const overCreditLimit = creditAccount && creditAccount.status === 'active' && creditAccount.available_credit < (cart.subtotal + (selectedShipping?.cost || 0));

  const handleCheckout = async () => {
    if (!selectedShipping) {
      toast.error('Please select a shipping method');
      return;
    }
    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postcode) {
      toast.error('Please fill in all shipping address fields');
      return;
    }
    
    // Check credit limit for account payments
    if (paymentMethod === 'account' && overCreditLimit) {
      toast.error('Order exceeds available credit limit');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (paymentMethod === 'account') {
        // Pay on Account - charge to credit account
        const res = await axios.post(`${API}/checkout/account-order`, {
          shipping_address: shippingAddress,
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          shipping_method: selectedShipping.method,
          shipping_cost: selectedShipping.cost,
          shipping_courier: selectedShipping.courier || selectedShipping.service_code,
          transdirect_booking_id: tdBookingId,
          notes: orderNotes,
        }, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        toast.success('Order placed on account!');
        navigate(`/order-confirmation/${res.data.order_id}`);
      } else if (paymentMethod === 'cash') {
        // Cash/Invoice payment - create order directly without Stripe
        const res = await axios.post(`${API}/checkout/cash-order`, {
          shipping_address: shippingAddress,
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          shipping_method: selectedShipping.method,
          shipping_cost: selectedShipping.cost,
          shipping_courier: selectedShipping.courier || selectedShipping.service_code,
          transdirect_booking_id: tdBookingId,
          notes: orderNotes,
        }, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        toast.success('Order placed successfully!');
        navigate(`/order-confirmation/${res.data.order_id}`);
      } else {
        // Card payment via Stripe
        const res = await axios.post(`${API}/checkout/create-session`, {
          shipping_address: shippingAddress,
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          shipping_method: selectedShipping.method,
          shipping_cost: selectedShipping.cost,
          shipping_courier: selectedShipping.courier || selectedShipping.service_code,
          transdirect_booking_id: tdBookingId,
          notes: orderNotes,
        }, {
          headers: { 'Origin': window.location.origin }
        });
        
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const shippingCost = selectedShipping?.cost || 0;
  const total = cart.subtotal + shippingCost;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
        <h1 className="text-3xl font-black text-foreground uppercase tracking-tight mb-8">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Address */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center mb-6">
                <Truck className="h-5 w-5 text-[#78BE20] mr-2" />
                <h2 className="text-lg font-bold text-foreground">Shipping Address</h2>
              </div>
              
              {/* Saved Addresses Selector */}
              {savedAddresses.length > 0 && (
                <div className="mb-6">
                  <Label className="text-sm text-muted-foreground">Use saved address</Label>
                  <Select
                    value={selectedSavedAddress}
                    onValueChange={handleSelectSavedAddress}
                  >
                    <SelectTrigger className="mt-1 bg-background border-border" data-testid="saved-address-select">
                      <SelectValue placeholder="Select a saved address" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedAddresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.id}>
                          {addr.label || 'Address'}: {addr.street}, {addr.city} {addr.state} {addr.postcode}
                        </SelectItem>
                      ))}
                      <SelectItem value="new">+ Enter new address</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Address Autocomplete */}
              <div className="mb-4">
                <Label className="text-sm text-muted-foreground">Search for address</Label>
                <AddressAutocomplete 
                  onAddressSelect={handleShippingAddressSelect}
                  placeholder="Start typing your address..."
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Type to auto-fill (AU & NZ)</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Street Address</Label>
                  <Input
                    value={shippingAddress.street}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                    className="mt-1 bg-background border-border"
                    required
                    data-testid="shipping-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      className="mt-1 bg-background border-border"
                      required
                      data-testid="shipping-city"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select
                      value={shippingAddress.state}
                      onValueChange={(v) => setShippingAddress({ ...shippingAddress, state: v })}
                    >
                      <SelectTrigger className="mt-1 bg-background border-border" data-testid="shipping-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {shippingStates.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={shippingAddress.postcode}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, postcode: e.target.value })}
                      className="mt-1 bg-background border-border"
                      required
                      maxLength={4}
                      data-testid="shipping-postcode"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input 
                      value={shippingAddress.country} 
                      disabled 
                      className="mt-1 bg-zinc-900 border-border" 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Method */}
            {shippingMethods.length > 0 && (
              <div className="bg-card border border-border rounded-sm p-6">
                <h2 className="text-lg font-bold text-foreground mb-4">Shipping Method</h2>
                <RadioGroup
                  value={selectedShipping?.method}
                  onValueChange={(v) => setSelectedShipping(shippingMethods.find(m => m.method === v))}
                >
                  {shippingMethods.map((method) => (
                    <div
                      key={method.method}
                      className="flex items-center justify-between p-4 border border-border rounded-sm cursor-pointer hover:border-[#78BE20] transition-colors"
                      onClick={() => setSelectedShipping(method)}
                      data-testid={`shipping-method-${method.service_code || method.method}`}
                    >
                      <div className="flex items-center">
                        <RadioGroupItem value={method.method} id={method.method} className="mr-3" />
                        <div>
                          <Label htmlFor={method.method} className="cursor-pointer font-medium">
                            {method.method}
                          </Label>
                          <p className="text-sm text-muted-foreground">{method.days}</p>
                          {method.description && (
                            <p className="text-xs text-muted-foreground/70">{method.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-bold font-mono text-[#78BE20]">
                          ${method.cost.toFixed(2)}
                        </span>
                        {method.carrier && (
                          <p className="text-xs text-muted-foreground">{method.carrier}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Billing Address */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 text-[#78BE20] mr-2" />
                  <h2 className="text-lg font-bold text-foreground">Billing Address</h2>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsShipping}
                    onChange={(e) => setSameAsShipping(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-muted-foreground">Same as shipping</span>
                </label>
              </div>
              
              {!sameAsShipping && (
                <div className="space-y-4">
                  {/* Address Autocomplete for Billing */}
                  <div>
                    <Label className="text-sm text-muted-foreground">Search for address</Label>
                    <AddressAutocomplete 
                      onAddressSelect={handleBillingAddressSelect}
                      placeholder="Start typing your billing address..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Street Address</Label>
                    <Input
                      value={billingAddress.street}
                      onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
                      className="mt-1 bg-background border-border"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={billingAddress.city}
                        onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                        className="mt-1 bg-background border-border"
                        required
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Select
                        value={billingAddress.state}
                        onValueChange={(v) => setBillingAddress({ ...billingAddress, state: v })}
                      >
                        <SelectTrigger className="mt-1 bg-background border-border">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {billingStates.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Postcode</Label>
                      <Input
                        value={billingAddress.postcode}
                        onChange={(e) => setBillingAddress({ ...billingAddress, postcode: e.target.value })}
                        className="mt-1 bg-background border-border"
                        required
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input 
                        value={billingAddress.country} 
                        disabled 
                        className="mt-1 bg-zinc-900 border-border" 
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold text-foreground mb-4">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.items?.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.product_name} x {item.quantity}
                    </span>
                    <span className="font-mono">${item.total_price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">${cart.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-mono">
                    {shippingCost > 0 ? `$${shippingCost.toFixed(2)}` : 'Calculate above'}
                  </span>
                </div>
              </div>

              <div className="border-t border-border mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-[#78BE20] font-mono">${total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">GST included</p>
              </div>

              {/* Payment Method Selection */}
              <div className="mt-6 space-y-3">
                <Label className="text-sm font-medium">Payment Method</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div 
                    className={`flex items-center p-3 border rounded-sm cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-[#78BE20] bg-[#78BE20]/10' : 'border-border'}`}
                    onClick={() => setPaymentMethod('card')}
                  >
                    <RadioGroupItem value="card" id="pay-card" className="mr-3" />
                    <div className="flex items-center">
                      <CreditCard className="h-4 w-4 mr-2 text-muted-foreground" />
                      <Label htmlFor="pay-card" className="cursor-pointer">Credit/Debit Card</Label>
                    </div>
                  </div>
                  <div 
                    className={`flex items-center p-3 border rounded-sm cursor-pointer transition-colors ${paymentMethod === 'cash' ? 'border-[#78BE20] bg-[#78BE20]/10' : 'border-border'}`}
                    onClick={() => setPaymentMethod('cash')}
                  >
                    <RadioGroupItem value="cash" id="pay-cash" className="mr-3" />
                    <div className="flex items-center">
                      <Truck className="h-4 w-4 mr-2 text-muted-foreground" />
                      <Label htmlFor="pay-cash" className="cursor-pointer">Cash / Invoice on Delivery</Label>
                    </div>
                  </div>
                  
                  {/* Pay on Account option - only for credit customers */}
                  {creditAccount && (
                    <div 
                      className={`flex items-center justify-between p-3 border rounded-sm transition-colors ${
                        !canPayOnAccount 
                          ? 'opacity-50 cursor-not-allowed border-border' 
                          : paymentMethod === 'account' 
                            ? 'border-[#78BE20] bg-[#78BE20]/10 cursor-pointer' 
                            : 'border-border cursor-pointer'
                      }`}
                      onClick={() => canPayOnAccount && setPaymentMethod('account')}
                    >
                      <div className="flex items-center">
                        <RadioGroupItem 
                          value="account" 
                          id="pay-account" 
                          className="mr-3" 
                          disabled={!canPayOnAccount}
                        />
                        <div>
                          <Label htmlFor="pay-account" className={`cursor-pointer ${!canPayOnAccount ? 'cursor-not-allowed' : ''}`}>
                            Pay on Account
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {creditAccount.payment_terms?.replace('_', ' ').toUpperCase()} • Available: ${creditAccount.available_credit?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {overCreditLimit && (
                        <span className="text-xs text-red-400">Exceeds limit</span>
                      )}
                    </div>
                  )}
                </RadioGroup>
              </div>

              {/* Order Notes */}
              <div className="mt-6">
                <Label className="text-muted-foreground text-sm">Order Notes (optional)</Label>
                <Textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="e.g., Leave at front door, call on arrival, delivery instructions..."
                  className="mt-1 bg-zinc-800 border-zinc-700 text-foreground text-sm resize-none"
                  rows={2}
                  data-testid="order-notes-input"
                />
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading || !selectedShipping || (paymentMethod === 'account' && !canPayOnAccount)}
                className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12 mt-6"
                data-testid="pay-btn"
              >
                <Lock className="h-4 w-4 mr-2" />
                {loading ? 'Processing...' : 
                  paymentMethod === 'account' ? 'Place Order on Account' :
                  paymentMethod === 'cash' ? 'Place Order' : 'Pay with Stripe'}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                {paymentMethod === 'account'
                  ? `Charged to your account • Payment due per ${creditAccount?.payment_terms?.replace('_', ' ')} terms`
                  : paymentMethod === 'cash' 
                    ? 'You will receive an invoice for payment' 
                    : 'Secure payment powered by Stripe'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
